import React, { createContext, useState, useEffect, useContext, useRef } from 'react';

const ThemeContext = createContext();

// Frases motivacionais para cada mês
const mensagensMensais = [
  "Janeiro incrível! Meta superada, rumo a novos desafios!", // Janeiro (0)
  "Fevereiro fantástico! O time não para de crescer!",      // Fevereiro (1)
  "Março espetacular! Batemos a meta com folga!",           // Março (2)
  "Abril sensacional! Seguimos quebrando recordes!",        // Abril (3)
  "Maio extraordinário! A equipe está imparável!",          // Maio (4)
  "Junho fenomenal! Nosso trabalho está dando frutos!",     // Junho (5)
  "Julho surpreendente! Superamos todas as expectativas!",  // Julho (6)
  "Agosto impressionante! Estamos fazendo história!",       // Agosto (7)
  "Setembro inigualável! Nosso esforço foi recompensado!",  // Setembro (8)
  "Outubro formidável! Batemos a meta novamente!",          // Outubro (9)
  "Novembro excepcional! A equipe merece aplausos!",        // Novembro (10)
  "Dezembro glorioso! Fechamos o ano com chave de ouro!"    // Dezembro (11)
];

export const ThemeProvider = ({ children }) => {
  // Verificar se o tema está salvo no localStorage ou usar a preferência do sistema
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    // Se já tiver um tema salvo, usar ele
    if (savedTheme && (savedTheme === 'dark' || savedTheme === 'light')) {
      return savedTheme === 'dark';
    }
    // Se não, verificar a preferência do sistema
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Estado para controlar o tema "meta batida"
  const [metaBatidaMode, setMetaBatidaMode] = useState(() => {
    return localStorage.getItem('theme') === 'meta-batida';
  });
  
  // Estado para controlar a exibição dos fogos de artifício
  const [showFireworks, setShowFireworks] = useState(false);
  
  // Referência para o timer do botão pressionado
  const buttonPressTimer = useRef(null);
  
  // Referência para o momento em que o botão foi pressionado
  const buttonPressStartTime = useRef(null);

  // Atualizar o tema quando ele mudar
  useEffect(() => {
    if (metaBatidaMode) {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('meta-batida');
      localStorage.setItem('theme', 'meta-batida');
    } else if (darkMode) {
      document.documentElement.classList.remove('meta-batida');
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.remove('meta-batida');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode, metaBatidaMode]);

  // Limpar o temporizador quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (buttonPressTimer.current) {
        clearTimeout(buttonPressTimer.current);
      }
    };
  }, []);

  // Função para lidar com o botão sendo pressionado
  const handleButtonPress = () => {
    buttonPressStartTime.current = Date.now();
    buttonPressTimer.current = setTimeout(() => {
      setMetaBatidaMode(true);
      setDarkMode(false);
      setShowFireworks(true);
      
      // Ocultar os fogos após 5 segundos
      setTimeout(() => {
        setShowFireworks(false);
      }, 5000);
    }, 3000); // 3 segundos
  };

  // Função para lidar com o botão sendo solto
  const handleButtonRelease = () => {
    // Se o botão for solto antes dos 3 segundos, cancele a ação e alterne o tema normal
    if (buttonPressTimer.current) {
      clearTimeout(buttonPressTimer.current);
      
      // Se o botão foi pressionado por menos de 500ms, consideramos um clique normal
      if (Date.now() - buttonPressStartTime.current < 500) {
        if (metaBatidaMode) {
          setMetaBatidaMode(false);
          setDarkMode(false);
        } else {
          setDarkMode(prevMode => !prevMode);
        }
      }
    }
  };

  // Obter a mensagem do mês atual
  const getMensagemAtual = () => {
    const mesAtual = new Date().getMonth();
    return mensagensMensais[mesAtual];
  };

  return (
    <ThemeContext.Provider 
      value={{ 
        darkMode, 
        toggleTheme: () => {
          if (metaBatidaMode) {
            setMetaBatidaMode(false);
            setDarkMode(false);
          } else {
            setDarkMode(prevMode => !prevMode);
          }
        },
        metaBatidaMode,
        showFireworks,
        mensagemMeta: getMensagemAtual(),
        handleButtonPress,
        handleButtonRelease
      }}
    >
      {children}
      {showFireworks && (
        <div className="fireworks-container">
          <div className="firework"></div>
          <div className="firework"></div>
          <div className="firework"></div>
          <div className="firework"></div>
          <div className="firework"></div>
          <div className="mensagem-meta">{getMensagemAtual()}</div>
        </div>
      )}
    </ThemeContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }
  return context;
};

export default ThemeContext; 